import * as z from 'zod'

import finRecordBuilderSchema, {
  type FinRecordBuilder,
} from './finRecordBuilder'
import { idNoUnderscoreSchema } from './primitives'
import questionValidationSchema from './question'
import tuningSchema from './tuning'
import createUniqueIdRefine from './zodHelpers/createUniqueIdRefine'

const sectionsSchema = z.strictObject({
  title: z.string().nullish(),
  questions: z.array(questionValidationSchema),
})

const subtopicSchema = z
  .strictObject({
    id: idNoUnderscoreSchema,
    title: z.string(),
    shortTitle: z.string().optional(),
    icon: z.string().nullable().default(null),
    renderIf: z.string().optional(),
    sections: z.array(sectionsSchema).min(1),
  })
  .superRefine(
    createUniqueIdRefine({
      getScope: data => data.sections,
      getUniqItemsInScope: section => section.questions,
      errorMessage: (key, id) =>
        `${key}.id "${id}" is duplicated within the same subtopic`,
    })
  )

const topicSchema = z
  .strictObject({
    id: idNoUnderscoreSchema,
    title: z.string(),
    description: z.string().nullish(),
    icon: z.string(),
    sections: z.array(sectionsSchema).min(1),
    finRecordBuilder: finRecordBuilderSchema,
    subtopics: z.array(subtopicSchema).default([]),
    // Quest-only: picker slots for tuning existing plan records.
    tunings: z.array(tuningSchema).default([]),
  })
  .superRefine(
    createUniqueIdRefine({
      getScope: data => data.sections,
      getUniqItemsInScope: section => section.questions,
      errorMessage: (key, id) =>
        `${key}.id "${id}" is duplicated within the same topic`,
    })
  )
  .superRefine((topic, ctx) => {
    type Owner = { kind: 'topic' | 'subtopic'; id: string }
    const seen = new Map<string, Owner>()
    const visit = (
      questionId: string,
      owner: Owner,
      path: (string | number)[]
    ) => {
      const existing = seen.get(questionId)
      if (!existing) {
        seen.set(questionId, owner)
        return
      }
      const sameOwner = existing.kind === owner.kind && existing.id === owner.id
      if (sameOwner) return
      const firstOwner = `${existing.kind} "${existing.id}"`
      const dupOwner = `${owner.kind} "${owner.id}"`
      ctx.addIssue({
        code: 'custom',
        path,
        message: `questions.id "${questionId}" is duplicated across the topic tree (first in ${firstOwner}, duplicate in ${dupOwner})`,
      })
    }
    topic.sections.forEach((section, sectionIndex) => {
      section.questions.forEach((question, questionIndex) => {
        visit(question.id, { kind: 'topic', id: topic.id }, [
          'sections',
          sectionIndex,
          'questions',
          questionIndex,
          'id',
        ])
      })
    })
    topic.subtopics.forEach((subtopic, subtopicIndex) => {
      subtopic.sections.forEach((section, sectionIndex) => {
        section.questions.forEach((question, questionIndex) => {
          visit(question.id, { kind: 'subtopic', id: subtopic.id }, [
            'subtopics',
            subtopicIndex,
            'sections',
            sectionIndex,
            'questions',
            questionIndex,
            'id',
          ])
        })
      })
    })
  })
  .superRefine((topic, ctx) => {
    const seen = new Map<string, number>()
    topic.subtopics.forEach((subtopic, index) => {
      const previous = seen.get(subtopic.id)
      if (previous !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['subtopics', index, 'id'],
          message: `subtopics.id "${subtopic.id}" is duplicated within the same topic. First occurrence in subtopics[${previous}]`,
        })
      } else {
        seen.set(subtopic.id, index)
      }
    })
  })
  .superRefine((topic, ctx) => {
    const subtopicIds = new Set(topic.subtopics.map(s => s.id))

    if (topic.finRecordBuilder.subtopicId !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['finRecordBuilder', 'subtopicId'],
        message:
          'subtopicId is only allowed on direct children of topic.finRecordBuilder.children',
      })
    }

    const level1Children = topic.finRecordBuilder.children ?? []
    level1Children.forEach((child, index) => {
      if (child.subtopicId !== undefined) {
        if (!subtopicIds.has(child.subtopicId)) {
          ctx.addIssue({
            code: 'custom',
            path: ['finRecordBuilder', 'children', index, 'subtopicId'],
            message: `subtopicId "${child.subtopicId}" does not match any subtopic in topic "${topic.id}"`,
          })
        }
      }
    })

    const visitDescendants = (
      builder: FinRecordBuilder,
      path: (string | number)[]
    ) => {
      const descendants = builder.children ?? []
      descendants.forEach((descendant, index) => {
        const descendantPath = [...path, 'children', index]
        if (descendant.subtopicId !== undefined) {
          ctx.addIssue({
            code: 'custom',
            path: [...descendantPath, 'subtopicId'],
            message:
              'subtopicId is only allowed on direct children of topic.finRecordBuilder.children',
          })
        }
        visitDescendants(descendant, descendantPath)
      })
    }
    level1Children.forEach((child, index) => {
      visitDescendants(child, ['finRecordBuilder', 'children', index])
    })
  })

export type Section = z.output<typeof sectionsSchema>

export type Subtopic = z.output<typeof subtopicSchema>

export type Topic = z.output<typeof topicSchema>

export { sectionsSchema, subtopicSchema }

export default topicSchema
